import { TableContainer, Table, Paper, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

/**
 * Plastic tax table.
 * @param {Object} props - Props for the component.
 * @param {number} props.surfaceWeight - Weight of the packaging surface per m² (in kg/m²).
 * @param {number} props.surface - Surface of packaging in a product (in m²).
 * @param {number} props.plasticPercentage - Percentage of plastic in the product packaging.
 * @param {number} props.recycledPlasticPercentage - Percentage of recycled plastic out of the total plastic in the product packaging.
 */
function ProductOrganizationPlastic({ surfaceWeight = null, pieceSurface = null, plasticPercentage = 0, recycledPlasticPercentage = 1 }) {
  const { t } = useTranslation();

  function format(value) {
    return parseFloat(value.toFixed(15));
  }

  const weight = surfaceWeight * pieceSurface; // Weight in kg given surface weight in kg/m² and surface in m²
  const plasticWeight = weight * plasticPercentage; // Weight of plastic in kg given weight in kg and plastic percentage
  const plasticSurfaceWeight = surfaceWeight * plasticPercentage; // Weight of plastic per m² given surface weight in kg/m² and plastic percentage

  const spain = 0.45 * (1 - recycledPlasticPercentage);
  const uk = recycledPlasticPercentage >= 0.3 ? 0 : 0.21785;
  const rows = [
    {
      name: t('organizations.product.lots.plastic.countries.spain'),
      currency: '€',
      costByPiece: format(spain * plasticWeight),
      costBySquareMeter: format(spain * plasticSurfaceWeight),
      costByKg: format(spain * plasticPercentage),
      exempt: recycledPlasticPercentage >= 1,
    },
    {
      name: t('organizations.product.lots.plastic.countries.uk'),
      currency: '£',
      costByPiece: format(uk * weight),
      costBySquareMeter: format(uk * surfaceWeight),
      costByKg: format(uk),
      exempt: recycledPlasticPercentage >= 0.3,
    },
  ];

  const StyledTableCell = styled(TableCell)(({ align }) => ({
    fontSize: 16,
    paddingTop: 12,
    paddingBottom: 12,
    textAlign: align || 'right',
  }));

  const HeadTableCell = styled(StyledTableCell)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  }));

  const StyledTableRow = styled(TableRow)(({ theme, disabled }) => ({
    backgroundColor: disabled ? theme.palette.action.disabled : undefined,
    '&:nth-of-type(odd)': {
      backgroundColor: disabled ? theme.palette.action.disabled : theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

  return (
    <TableContainer component={Paper} sx={{ width: 'fit-content', maxWidth: '100%' }}>
      <Table aria-label="plastic tax table">
        <TableHead>
          <TableRow>
            <HeadTableCell align="left">{t('organizations.product.lots.plastic.country')}</HeadTableCell>
            <HeadTableCell>
              {t('organizations.product.lots.plastic.cost')}/{t('organizations.product.lots.plastic.pc')}
            </HeadTableCell>
            <HeadTableCell>{t('organizations.product.lots.plastic.cost')}/m²</HeadTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) =>
            row.exempt ? (
              <StyledTableRow disabled key={row.name}>
                <StyledTableCell component="th" scope="row" align="left">
                  {row.name}
                </StyledTableCell>
                <StyledTableCell align="center" colSpan={3}>
                  {t('organizations.product.lots.plastic.exempt')}
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              <StyledTableRow key={row.name}>
                <StyledTableCell component="th" scope="row" align="left">
                  {row.name}
                </StyledTableCell>
                {pieceSurface && surfaceWeight ? (
                  <StyledTableCell>
                    {row.currency}
                    {row.costByPiece}
                  </StyledTableCell>
                ) : (
                  <StyledTableCell>-</StyledTableCell>
                )}
                {surfaceWeight ? (
                  <StyledTableCell>
                    {row.currency}
                    {row.costBySquareMeter}
                  </StyledTableCell>
                ) : (
                  <StyledTableCell>-</StyledTableCell>
                )}
              </StyledTableRow>
            )
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

ProductOrganizationPlastic.propTypes = {
  surfaceWeight: PropTypes.number,
  pieceSurface: PropTypes.number,
  plasticPercentage: PropTypes.number,
  recycledPlasticPercentage: PropTypes.number,
};

export default ProductOrganizationPlastic;
